import { useMemo, useState } from 'react';

import { Box, Typography, IconButton, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../../utils/i18n';

export default function FuelChartBox({ isOpen, onClose, allWaypoints = [], licensePlate = '-', fuelCapacity = 0, isEV = false }) {
  const { t } = useLanguage();
  const [viewType, setViewType] = useState('percentage'); // 'percentage' | 'litre'

  const chartColor = isEV ? '#2196f3' : '#4CAF50';

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };
  const { chartData, chartTicks, isMultiDay } = useMemo(() => {
    if (!allWaypoints || allWaypoints.length === 0) return { chartData: [], chartTicks: [], isMultiDay: false };

    let processData = allWaypoints;
    if (allWaypoints.length > 300) {
      const step = Math.ceil(allWaypoints.length / 300);
      processData = allWaypoints.filter((_, i) => i % step === 0);
    }

    // dayjs dipakai untuk clustering waktu
    const start = window.dayjs
      ? window.dayjs(processData[0].date_send || processData[0].start_wp_time)
      : new Date(processData[0].date_send || processData[0].start_wp_time);
    const end = window.dayjs
      ? window.dayjs(processData[processData.length - 1].date_send || processData[processData.length - 1].start_wp_time)
      : new Date(processData[processData.length - 1].date_send || processData[processData.length - 1].start_wp_time);

    let isMulti = false;
    if (window.dayjs) {
      isMulti = end.diff(start, 'hour') > 24;
    } else {
      isMulti = end - start > 24 * 60 * 60 * 1000;
    }

    let lastValidPct = null;
    let lastValidLit = null;

    let lastTick = null;
    const calculatedTicks = [];

    const calculatedData = processData.map((wp) => {
      // Fallback ke canbus jika property utama tidak ada
      let rawPct = wp.can_fuel_percentage ?? wp.canbus?.can_fuel_percentage ?? wp.canbus?.canFuelLevel ?? 0;
      let rawLit = wp.can_fuel_litre ?? wp.canbus?.canFuelConsumedCounted ?? wp.canbus?.can_fuel_litre ?? 0;

      // Parsing string ber-koma jika ada
      let pct = parseFloat(String(rawPct).replace(',', '.'));
      let lit = parseFloat(String(rawLit).replace(',', '.'));

      if (isNaN(pct)) pct = 0;
      if (isNaN(lit)) lit = 0;

      // Anomaly detection logic
      // Abnormal if drops to <= 0 when previously it was > 5, or if sudden drop > 20
      let isAbnormalPct = false;
      if (lastValidPct !== null) {
        if (pct <= 0 && lastValidPct > 5) isAbnormalPct = true;
        if (lastValidPct - pct > 20) isAbnormalPct = true;
      }

      if (isAbnormalPct) {
        pct = lastValidPct;
      } else {
        lastValidPct = pct;
      }

      // Litre anomaly
      let isAbnormalLit = false;
      if (lastValidLit !== null) {
        if (lit <= 0 && lastValidLit > 10) isAbnormalLit = true;
        // Assume > 20% of capacity drop is abnormal, or roughly 50 litres drop for large tanks
        const dropThreshold = fuelCapacity ? fuelCapacity * 0.2 : 50;
        if (lastValidLit - lit > dropThreshold) isAbnormalLit = true;
      }

      if (isAbnormalLit) {
        lit = lastValidLit;
      } else {
        lastValidLit = lit;
      }

      const timeString = wp.date_send || wp.start_wp_time || '';

      // Hitung ticks per 1 jam atau per 1 hari
      if (timeString) {
        if (window.dayjs) {
          const d = window.dayjs(timeString);
          if (!lastTick) {
            calculatedTicks.push(timeString);
            lastTick = d;
          } else {
            if (isMulti) {
              if (d.diff(lastTick, 'day') >= 1 || d.date() !== lastTick.date()) {
                calculatedTicks.push(timeString);
                lastTick = d;
              }
            } else {
              if (d.diff(lastTick, 'hour') >= 1 || d.hour() !== lastTick.hour()) {
                calculatedTicks.push(timeString);
                lastTick = d;
              }
            }
          }
        } else {
          // Fallback native Date
          const d = new Date(timeString);
          if (!lastTick) {
            calculatedTicks.push(timeString);
            lastTick = d;
          } else {
            if (isMulti) {
              if (d.getDate() !== lastTick.getDate()) {
                calculatedTicks.push(timeString);
                lastTick = d;
              }
            } else {
              if (d.getHours() !== lastTick.getHours()) {
                calculatedTicks.push(timeString);
                lastTick = d;
              }
            }
          }
        }
      }

      return {
        ...wp,
        fullTime: timeString,
        cleanPct: pct,
        cleanLit: lit
      };
    });

    return { chartData: calculatedData, chartTicks: calculatedTicks, isMultiDay: isMulti };
  }, [allWaypoints, fuelCapacity]);

  const displayCapacity = useMemo(() => {
    let cap = Number(fuelCapacity);
    if (!isNaN(cap) && cap > 0) return cap;
    if (allWaypoints && allWaypoints.length > 0) {
      const vCap = Number(allWaypoints[0]?.vehicle?.fuel_capacity);
      if (!isNaN(vCap) && vCap > 0) return vCap;
    }
    return 0;
  }, [fuelCapacity, allWaypoints]);

  const yAxisTicks = useMemo(() => {
    const ticks = [];
    if (viewType === 'percentage') {
      for (let i = 0; i <= 10; i++) {
        ticks.push(i * 10);
      }
    } else {
      let maxLitre = displayCapacity;
      if (maxLitre <= 0) {
        maxLitre = Math.max(...chartData.map((d) => d.cleanLit), 10);
      }
      const step = maxLitre / 10;
      for (let i = 0; i <= 10; i++) {
        ticks.push(Math.round(i * step));
      }
    }
    return ticks;
  }, [viewType, displayCapacity, chartData]);

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '100%',
        right: 0,
        mb: 1,
        width: { xs: 300, sm: 450, md: 600 },
        height: 300,
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        animation: 'fadeIn 0.2s ease-out',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color="#1e293b">
            {isEV ? t('mapsTracking.energyHistory', 'Energy History') : t('mapsTracking.fuelHistory', 'Fuel History')}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">
            {licensePlate} {displayCapacity ? `| ${t('mapsTracking.capacity', 'Capacity:')} ${displayCapacity} ${isEV ? 'kWh' : 'L'}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup value={viewType} exclusive onChange={handleViewChange} size="small" sx={{ height: 30 }}>
            <ToggleButton value="percentage" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              {t('mapsTracking.percentage', 'Percentage (%)')}
            </ToggleButton>
            <ToggleButton value="litre" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              {isEV ? t('mapsTracking.energyKwh', 'Energy (Kwh)') : t('mapsTracking.litreL', 'Litre (L)')}
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: '#f60000', bgcolor: 'rgba(246, 0, 0, 0.1)', '&:hover': { bgcolor: 'rgba(246, 0, 0, 0.2)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorFuel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis
              dataKey="fullTime"
              ticks={chartTicks.length > 0 ? chartTicks : undefined}
              tickFormatter={(val) => {
                if (!val) return '';
                if (window.dayjs) {
                  return isMultiDay ? window.dayjs(val).format('DD MMM') : window.dayjs(val).format('HH:mm');
                }
                // Fallback formatter native Date
                const d = new Date(val);
                return isMultiDay
                  ? `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`
                  : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
              }}
              tick={{ fontSize: 10 }}
              tickMargin={10}
            />
            <YAxis
              type="number"
              tickCount={11}
              width={45}
              tick={{ fontSize: 10 }}
              domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              interval={0}
            />
            <RechartsTooltip
              contentStyle={{ fontSize: '0.8rem', borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              labelStyle={{ fontWeight: 'bold', color: '#333', marginBottom: 4 }}
              labelFormatter={(label) => {
                if (!label) return '';
                if (window.dayjs) return window.dayjs(label).format('DD-MM-YYYY HH:mm:ss');
                return label;
              }}
              formatter={(value) => [
                Number(value).toFixed(2),
                viewType === 'percentage' ? (isEV ? 'Energy (%)' : 'Fuel (%)') : isEV ? 'Energy (kWh)' : 'Fuel (L)'
              ]}
            />
            <Area
              type="monotone"
              dataKey={viewType === 'percentage' ? 'cleanPct' : 'cleanLit'}
              stroke={chartColor}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorFuel)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
